import { Box, capitalize } from '@mui/material';
import * as Sentry from '@sentry/react';
import dayjs from 'dayjs';
import HTMLReactParser, { Element } from 'html-react-parser';
import type { ReactNode } from 'react';
import { isCurrencyField, minFractionDigitsMap, numberFieldsObj } from './utils';

export const isNumber = (value?: string) => (value ? numberFieldsObj[value.toLowerCase()] : false);

const availableCurrencies: Record<string, string> = {
  '&pound;': '£',
  $: '$',
  AU$: 'AU$',
  CA$: 'CA$',
  S$: 'S$',
  Rs: 'Rs',
  '&euro': '€',
  HK$: 'HK$',
  CHF: 'CHF',
  MYR: 'MYR',
  ZAR: 'ZAR',
  INR: 'INR',
  US$: 'US$',
  SGD: 'SGD',
  A$: 'A$',
};

const numericCellSx = { fontVariantNumeric: 'tabular-nums' } as const;

function NumberCell({ value, accessor }: { value: number; accessor?: string }) {
  const prettyValue = value.toLocaleString(navigator.language, {
    maximumFractionDigits: 2,
    minimumFractionDigits: accessor ? minFractionDigitsMap[accessor.toLowerCase()] : 0,
  });
  const color = value < 0 ? 'error.main' : undefined;

  return (
    <Box component="span" sx={color ? { ...numericCellSx, color } : numericCellSx}>
      {prettyValue}
    </Box>
  );
}

function CurrencyCell({ value, currency }: { value: number; currency: string }) {
  const formatted = `${currency}${value.toLocaleString(navigator.language, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
  const color = value < 0 ? 'error.main' : undefined;

  return (
    <Box component="span" sx={color ? { ...numericCellSx, color } : numericCellSx}>
      {formatted}
    </Box>
  );
}

function DateCell({ value, accessor }: { value: string; accessor?: string }) {
  const mDate = dayjs(value);
  if (!mDate.isValid()) return <>{value}</>;

  if (accessor === 'yyyymm') return <>{mDate.format('MMMM YYYY')}</>;

  const formatType = navigator.language === 'en-US' ? 'MM/DD/YYYY' : 'DD/MM/YYYY';
  return <>{mDate.format(formatType)}</>;
}

const creativeSx = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  minWidth: '300px',
  '@media screen and (max-width: 1560px)': {
    flexWrap: 'wrap',
    minWidth: '200px',
  },
} as const;

function CreativeCell({ html }: { html: string }) {
  if (typeof html !== 'string') return html;
  return <Box sx={creativeSx}>{HTMLReactParser(html)}</Box>;
}

function HtmlContentCell({ html }: { html: string }) {
  return (
    <div style={{ textAlign: 'center' }}>
      {HTMLReactParser(html, {
        replace: (node) => {
          if (node instanceof Element && node.name === 'script') {
            Sentry.captureException(new Error('script tags not allowed in trusted html'), {
              extra: { value: html },
            });
            return null;
          }
          return node;
        },
      })}
    </div>
  );
}

function CurrencySymbolCell({ value }: { value: string }) {
  return <>{availableCurrencies[value] || null}</>;
}

type TableCellProps = {
  column: {
    accessor?: string;
    header: string;
  };
  value: string | number | null | ReactNode;
  currency?: string;
  formatNumbers?: boolean;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  row: Record<string, any>;
};

type ResolvedColumn =
  | { kind: 'reactNode'; node: ReactNode }
  | { kind: 'cell'; accessor: string | undefined; header: string; value: ReactNode };

/**
 * Resolves custom column accessors (e.g. "CreativeCustom.field") back to
 * their base accessor and value, handling pre-parsed ReactNode shortcuts.
 */
function resolveCustomColumn(
  accessor: string | undefined,
  header: string,
  value: ReactNode,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  row: Record<string, any>,
): ResolvedColumn {
  if (!accessor?.includes('Custom.')) return { kind: 'cell', accessor, header, value };

  const [topKey] = accessor.split('.');
  const val = row[topKey];
  if (val && typeof val === 'object' && 'ReactNode' in val && val.ReactNode) {
    return { kind: 'reactNode', node: val.ReactNode };
  }

  const [baseAccessor] = accessor.split('Custom.');
  return {
    kind: 'cell',
    accessor: baseAccessor,
    header: capitalize(baseAccessor),
    value: row[baseAccessor],
  };
}

function rewritePreviewUrls(val: ReactNode): ReactNode {
  if (process.env.REACT_APP_ENVIRONMENT !== 'prod' && typeof val === 'string') {
    return val.replace('/img', '/preview/img');
  }
  return val;
}

function TableCell({
  column,
  value,
  currency = undefined,
  formatNumbers = false,
  row = {},
}: TableCellProps) {
  try {
    const resolved = resolveCustomColumn(column.accessor, column.header, value, row);
    if (resolved.kind === 'reactNode') return resolved.node;

    const { accessor, header, value: resolvedValue } = resolved;

    if (resolvedValue == null) return null;

    if (formatNumbers) {
      if (isNumber(accessor) && !Number.isNaN(Number(resolvedValue))) {
        return <NumberCell value={Number(resolvedValue)} accessor={accessor} />;
      }
      if (isCurrencyField(accessor)) {
        return <CurrencyCell value={Number(resolvedValue)} currency={currency ?? ''} />;
      }
    }

    const displayValue = rewritePreviewUrls(resolvedValue);

    switch (header?.toLowerCase()) {
      case 'currency':
        return <CurrencySymbolCell value={displayValue as string} />;
      case 'creative':
        return <CreativeCell html={displayValue as string} />;
      case 'image':
      case 'partner':
      case 'publisher':
      case 'img':
        return <HtmlContentCell html={displayValue as string} />;
      case 'date':
        return <DateCell value={displayValue as string} accessor={accessor} />;
      default:
        return <>{displayValue}</>;
    }
  } catch (e) {
    console.error(e);
    Sentry.withScope((scope) => {
      scope.setTags({
        message: 'Error trying to Format cell data()',
        file: 'TableCell.tsx',
        fn: 'TableCell',
      });
      Sentry.captureException(e);
    });
    return (
      <Box component="span" sx={{ color: 'text.disabled' }}>
        &mdash;
      </Box>
    );
  }
}

export default TableCell;
